import { cn } from '@/lib/utils'
import { useContrastedBackground } from 'app/frontend/base/components/Card'
import { useTeamEventUpdateContext } from '../context/TeamEventUpdateContext'
import {
  EventActivityParticipantEntry,
  EventActivityParticipantGroup,
} from './eventActivityParticipantEntry'
import EventParticipantContextMenu from './EventParticipantContextMenu'
import CharacterRoleIcon from 'app/frontend/base/components/CharacterRoleIcon'
import { CharacterRole } from 'app/frontend/types/custom'
import BlizzardIcon from 'app/frontend/base/components/BlizzardIcon'
import { getSpecializationsByClass } from 'app/frontend/base/services/characterValues'
import { CharacterClassIcon } from 'app/frontend/base/components'
import { classColours } from 'app/frontend/types/custom/CharacterClass'
import EventStatusIcon from 'app/frontend/base/components/EventStatusIcon'
import { resolveEventParticipantCharacter } from './eventActivityParticipants'
import { useTeamEventPlanningInfoContext } from '../context/TeamEventPlanningInfoContext'
import EventParticipantCommentIndicators from './EventParticipantCommentIndicators'
import { presentParticipantStatuses } from 'app/frontend/types/custom/EventParticipantStatus'
import { resolveSelectionOverrideDisplay } from './eventSelectionOverrideDisplay'
import EventActivityParticipantPlanningInfo from './EventActivityParticipantPlanningInfo'
import EventActivityParticipantActivityPlanningInfo from './EventActivityParticipantActivityPlanningInfo'
import EventActivitySelectedCount from './EventActivitySelectedCount'

type Props = {
  group: EventActivityParticipantGroup
  row: EventActivityParticipantEntry
  activities: wowaudit.EventActivity[]
  selectableStatuses: wowaudit.EventParticipantReference['status'][]
  canAdminEdit: boolean
  userId?: number
  hideStatus?: boolean
  hideEventActions?: boolean
  isNarrowActivityColumn?: boolean
}

const EventActivityGridRow = ({
  group,
  row,
  activities,
  selectableStatuses,
  canAdminEdit,
  userId,
  hideStatus = false,
  hideEventActions = false,
  isNarrowActivityColumn = false,
}: Props) => {
  const { updateSelection } = useTeamEventUpdateContext()
  const { showPlanningInfo } = useTeamEventPlanningInfoContext()
  const canToggle = canAdminEdit && !group.isUnavailable
  const background = useContrastedBackground()
  const rowCharacter = resolveEventParticipantCharacter(row)

  const classSpecializations = getSpecializationsByClass(rowCharacter.classId)
  const mainRole = row.role
  const mainSpecId = row.specId
  const spec = mainSpecId ? classSpecializations[mainSpecId] : undefined

  const rowClassBackground = classColours[rowCharacter.characterClass]

  const selectedCount = activities.filter(
    (activity) =>
      !group.isUnavailable &&
      activity.active &&
      row.selections[activity.id]?.selected,
  ).length
  const participantCellContent = (
    <div
      className={cn(
        'flex min-w-0 items-center gap-2',
        group.isUnavailable ? '' : rowClassBackground.text,
      )}
    >
      {hideStatus ? null : <EventStatusIcon status={row.participant.status} />}
      <span className='relative inline-flex'>
        {spec ? (
          <BlizzardIcon
            iconName={spec.icon}
            className={group.isUnavailable ? 'grayscale' : ''}
          />
        ) : (
          <CharacterClassIcon
            characterClass={rowCharacter.characterClass}
            className={group.isUnavailable ? 'grayscale' : ''}
          />
        )}
        {hideStatus ? null : (
          <EventParticipantCommentIndicators
            comment={row.participant.comment}
            adminComment={row.participant.adminComment}
          />
        )}
      </span>
      <span className='min-w-0 truncate'>{rowCharacter.name}</span>
    </div>
  )
  const participantCellTrigger = (
    <div
      className={cn(
        'flex w-full items-center justify-between gap-2 pl-2 pr-2 py-1',
        canAdminEdit && 'cursor-pointer',
      )}
    >
      {participantCellContent}
      <EventActivitySelectedCount
        selectedCount={selectedCount}
        className='text-base font-mono'
      />
    </div>
  )

  return (
    <>
      <tr
        className={cn(
          'group/row [&>td]:relative [&>td]:z-10 [&>td:last-child]:border-r [&>td:last-child]:rounded-r-lg',
          group.isUnavailable && '[&>td]:text-muted-foreground',
        )}
      >
        <td
          className={cn(
            'w-[190px] min-w-[190px] max-w-[190px] border rounded-lg text-sm group-hover/row:border-accent-foreground/45',
            !presentParticipantStatuses.includes(row.participant.status) &&
              !group.isUnavailable
              ? 'bg-warning hover:bg-warning'
              : row.participant.addedAsGuest
              ? 'bg-info hover:bg-info'
              : `group-hover/row:bg-card-nested ${background}`,
          )}
        >
          {canAdminEdit ? (
            <EventParticipantContextMenu
              inGrid
              entry={row}
              selectableStatuses={selectableStatuses}
              canEditStatus={!hideStatus && canAdminEdit}
              canAdminEdit={canAdminEdit}
              hideEventActions={hideEventActions}
              trigger={participantCellTrigger}
            />
          ) : (
            participantCellTrigger
          )}
        </td>

        {activities.map((activity, i) => {
          const isActivityInactive = !activity.active
          const selection = row.selections[activity.id]
          const isUnavailableCell = group.isUnavailable || isActivityInactive
          if (!selection) {
            return <td key={activity.id} className='border-l border-r' />
          }
          const entry = {
            ...row,
            ...selection,
          }
          const isSelected = !isUnavailableCell && entry.selected
          const displayCharacter = resolveEventParticipantCharacter(
            row,
            activity.id,
          )
          const selectedClassBackground =
            classColours[displayCharacter.characterClass]
          const isOwnCharacter =
            userId === entry.participant.characterReference.userId
          const canEditStatus = !hideStatus && (canAdminEdit || isOwnCharacter)
          const selectionOverrideDisplay = resolveSelectionOverrideDisplay({
            classId: displayCharacter.classId,
            role: entry.role,
            specId: entry.specId,
            mainRole,
            mainSpecId,
          })

          return (
            <td
              key={activity.id}
              className={cn(
                'border-l border-r relative text-sm transition-all duration-200 group py-1 border-y',
                background,
                activity.active ? '' : 'opacity-50',
                i === 0 ? 'rounded-l-lg' : '',
                isSelected &&
                  'hover:brightness-110 hover:saturate-110 hover:shadow-[inset_0_0_0_1px_rgba(255,255,255,0.18)]',
                isSelected
                  ? selectedClassBackground.active
                  : isUnavailableCell
                  ? ''
                  : selectedClassBackground.hover,
              )}
            >
              <div
                className={cn(
                  'absolute inset-0 grid grid-cols-[1fr_auto_1fr] items-center px-1 py-1 transition-colors',
                  isSelected
                    ? selectedClassBackground.activeText
                    : 'text-muted-foreground',
                  isUnavailableCell && 'opacity-70',
                )}
              >
                {!isUnavailableCell ? (
                  <button
                    type='button'
                    disabled={!canToggle}
                    className={cn(
                      'absolute inset-0 h-full w-full transition-colors',
                      canToggle && 'cursor-pointer',
                      !canToggle && 'cursor-default',
                    )}
                    onClick={() =>
                      updateSelection(
                        row.participant.id,
                        { selected: !entry.selected },
                        activity.id,
                      )
                    }
                  />
                ) : null}

                {!isUnavailableCell ? (
                  <span
                    className={cn(
                      'pointer-events-none z-10 col-start-2 flex items-center gap-1 transition-opacity',
                      isSelected ? 'opacity-100' : 'opacity-30',
                    )}
                  >
                    {selectionOverrideDisplay.showSpecIcon &&
                    selectionOverrideDisplay.specIcon ? (
                      <BlizzardIcon
                        iconName={selectionOverrideDisplay.specIcon}
                      />
                    ) : selectionOverrideDisplay.showRoleIcon ? (
                      <CharacterRoleIcon role={entry.role as CharacterRole} />
                    ) : null}
                  </span>
                ) : null}

                {canAdminEdit && !isUnavailableCell ? (
                  <span className='relative z-10 col-start-3 justify-self-end'>
                    <EventParticipantContextMenu
                      className={cn(
                        'bg-transparent  hover:bg-card-nested/30',
                        isSelected
                          ? 'text-current hover:text-current/80'
                          : 'hover:text-accent-foreground',
                      )}
                      inGrid
                      entry={entry}
                      activityId={activity.id}
                      selectableStatuses={selectableStatuses}
                      canEditStatus={canEditStatus}
                      canAdminEdit={canAdminEdit}
                      hideEventActions={hideEventActions}
                    />
                  </span>
                ) : null}
              </div>
            </td>
          )
        })}
      </tr>

      {showPlanningInfo && !group.isUnavailable ? (
        <tr className='[&>td]:relative [&>td]:z-0 [&>td]:align-top [&>td]:pt-0 [&>td]:pb-0.5'>
          <td>
            <EventActivityParticipantPlanningInfo
              entry={row}
              className='mx-1 -mt-[3px] rounded-t-none'
            />
          </td>
          {activities.map((activity) => {
            const isActivityInactive = !activity.active
            if (isActivityInactive) {
              return <td key={activity.id} />
            }
            const selection = row.selections[activity.id]
            if (!selection) {
              return <td key={activity.id} />
            }
            return (
              <td key={activity.id}>
                <EventActivityParticipantActivityPlanningInfo
                  entry={row}
                  activity={activity}
                  isNarrowColumn={isNarrowActivityColumn}
                  className='mx-1 -mt-[3px]'
                />
              </td>
            )
          })}
        </tr>
      ) : null}
    </>
  )
}

export default EventActivityGridRow
